import type { EventMap, EventName } from "./events";
import { trackWarning } from "../telemetry/appInsights";

type Listener<T> = (event: T) => void;

/**
 * Typed application event bus. Publishing "error" is the single path from a
 * caught error to telemetry plus a toast; subscribers are wired in main.tsx.
 */
class EventBus {
  private readonly listeners = new Map<EventName, Set<Listener<never>>>();

  subscribe<K extends EventName>(event: K, listener: Listener<EventMap[K]>): () => void {
    const existing = this.listeners.get(event) ?? new Set<Listener<never>>();
    existing.add(listener as Listener<never>);
    this.listeners.set(event, existing);
    return () => existing.delete(listener as Listener<never>);
  }

  publish<K extends EventName>(event: K, payload: EventMap[K]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const listener of set) {
      try {
        (listener as Listener<EventMap[K]>)(payload);
      } catch (subscriberError) {
        const message = subscriberError instanceof Error ? subscriberError.message : String(subscriberError);
        trackWarning(`EventBus subscriber failed for "${event}": ${message}`);
      }
    }
  }
}

export const eventBus = new EventBus();

export function toError(candidate: unknown): Error {
  return candidate instanceof Error ? candidate : new Error(String(candidate));
}

/** Publish a caught error with its owning interaction as context. */
export function reportError(candidate: unknown, context: string): Error {
  const error = toError(candidate);
  eventBus.publish("error", { error, context });
  return error;
}
